/* eslint-disable react/no-array-index-key */
import React from 'react';
import { flushSync } from 'react-dom';
import { get } from 'lodash-es';
import useEffect from '@/util/hook/useEffect';
import useRef from '@/util/hook/useRef';
import useState from '@/util/hook/useState';
import { SEMANTIC_COLORS } from '@/constant/unit/color';
import { S } from './style';
import { ChevronLeft, ChevronRight } from '../../atom/Icon';
const SLIDE_EVENT_TYPE = {
    PREV: 'PREV',
    NEXT: 'NEXT',
};
const SLIDE_GAP = 56;
const VISIBLE_SIBLING_SLIDE_WIDTH = 12;
const getSlideWidth = ({ carouselRef }) => {
    const item = get(carouselRef, 'current.firstChild');
    const slideWidth = item ? item.offsetWidth : 0;
    return { slideWidth };
};
const getTranslateValuePerSlide = ({ slideWidth }) => {
    const translateValuePerSlide = slideWidth + SLIDE_GAP;
    return { translateValuePerSlide };
};
const getTranslateValueForCurrentActiveItem = ({ slideWidth, currentSlideIndex, translateValuePerSlide, }) => {
    const translateValueForCurrentActiveItem = -(translateValuePerSlide * (currentSlideIndex - 1) +
        slideWidth -
        VISIBLE_SIBLING_SLIDE_WIDTH);
    return { translateValueForCurrentActiveItem };
};
/**
 * @description
 * Append cloned elements to the beginning and end of the carousel
 * to make the carousel infinite
 * and create cloned elements based on the original items with additional props.
 */
const getSlideItems = ({ items, activeItemId }) => {
    const extraItemCountPerSide = 2;
    const itemsLength = items.length;
    // e.g., items: [a, b, c, d]
    const extraPrevItems = items.slice(itemsLength - extraItemCountPerSide, itemsLength); // [c, d]
    const extraNextItems = items.slice(0, extraItemCountPerSide); // [a, b]
    const totalItems = extraPrevItems.concat(items).concat(extraNextItems); // [c, d, a, b, c, d, a, b]
    const slideItems = totalItems.map((source, index) => {
        const key = `slideClone-${index}`; // unique key
        const data = get(source, 'props.data');
        const id = `slideClone-${index % items.length}`; // items with same id share the same data
        const slideItem = React.cloneElement(source, {
            isActive: id === activeItemId,
            key,
            data,
            id,
        });
        return slideItem;
    });
    const processedItems = slideItems.slice(extraItemCountPerSide, extraItemCountPerSide + items.length); // [a, b, c, d]
    return { slideItems, processedItems };
};
/**
 * @description
 * Update the current slide index and the translateValue when the transition ends
 */
const handleTransitionEnd = ({ itemsLength, currentSlideIndex, firstItemIndexInSlideClones, firstItemTranslateValue, lastItemTranslateValue, setShouldStartTransition, setTranslateValue, setCurrentSlideIndex, setIsSlideDisabled, }) => {
    setShouldStartTransition(false);
    const lastItemIndex = firstItemIndexInSlideClones + itemsLength - 1;
    const clonedFirstItemIndex = firstItemIndexInSlideClones + itemsLength;
    const clonedLastItemIndex = 1;
    if (currentSlideIndex === clonedLastItemIndex) {
        setTranslateValue(lastItemTranslateValue);
        setCurrentSlideIndex(lastItemIndex);
    }
    if (currentSlideIndex === clonedFirstItemIndex) {
        setTranslateValue(firstItemTranslateValue);
        setCurrentSlideIndex(firstItemIndexInSlideClones);
    }
    setIsSlideDisabled(false);
};
const handleSlide = ({ eventType, isSlideDisabled, translateValuePerSlide, slideItems, currentSlideIndex, activeItemId, activeItemData, setActiveItemId, setActiveItemData, handleActiveItemChange, setTranslateValue, setShouldStartTransition, setCurrentSlideIndex, setIsSlideDisabled, }) => {
    if (isSlideDisabled)
        return;
    setIsSlideDisabled(true);
    setShouldStartTransition(true);
    const translateValuePayload = eventType === SLIDE_EVENT_TYPE.PREV ? translateValuePerSlide : -translateValuePerSlide;
    const slideIndexPayload = eventType === SLIDE_EVENT_TYPE.PREV ? -1 : 1;
    const newCurrentSlideIndex = currentSlideIndex + slideIndexPayload;
    const newCurrentItem = slideItems[newCurrentSlideIndex];
    const newCurrentItemData = get(newCurrentItem, 'props.data');
    const newCurrentItemId = get(newCurrentItem, 'props.id');
    if (activeItemId !== newCurrentItemId) {
        handleActiveItemChange({
            current: newCurrentItemData,
            prev: activeItemData || newCurrentItemData,
        });
    }
    setActiveItemData(newCurrentItemData);
    setActiveItemId(newCurrentItemId);
    setTranslateValue((prev) => prev + translateValuePayload);
    setCurrentSlideIndex((prev) => prev + slideIndexPayload);
};
const handleTouchStart = ({ firstTouch, setTouchPosition, }) => {
    const touchClientX = firstTouch.clientX;
    setTouchPosition(touchClientX);
};
const handleTouchMove = ({ firstTouch, touchDown, isSlideDisabled, translateValuePerSlide, setTranslateValue, setShouldStartTransition, setCurrentSlideIndex, setIsSlideDisabled, setTouchPosition, slideItems, currentSlideIndex, activeItemId, activeItemData, setActiveItemId, setActiveItemData, handleActiveItemChange, }) => {
    const SLIDE_DIFF_THRESHOLD = 50;
    const doIgnoreTouchMove = touchDown === null || isSlideDisabled;
    if (doIgnoreTouchMove) {
        return;
    }
    const currentTouch = firstTouch.clientX;
    const diff = touchDown - currentTouch;
    const doSlideToNext = diff > SLIDE_DIFF_THRESHOLD;
    const doSlideToPrev = diff < -SLIDE_DIFF_THRESHOLD;
    if (doSlideToNext || doSlideToPrev) {
        // #NOTES
        // we use flushSync to set 'touchPosition' null synchronously
        // to make sure the following code will be executed ONLY ONCE until
        // the next onTouchStart event is triggered
        flushSync(() => {
            setTouchPosition(null);
        });
    }
    let eventType = null;
    if (doSlideToNext) {
        eventType = SLIDE_EVENT_TYPE.NEXT;
    }
    else if (doSlideToPrev) {
        eventType = SLIDE_EVENT_TYPE.PREV;
    }
    if (eventType) {
        handleSlide({
            eventType,
            isSlideDisabled,
            translateValuePerSlide,
            slideItems,
            currentSlideIndex,
            activeItemId,
            activeItemData,
            setActiveItemId,
            setActiveItemData,
            handleActiveItemChange,
            setTranslateValue,
            setShouldStartTransition,
            setCurrentSlideIndex,
            setIsSlideDisabled,
        });
    }
};
/**
 * @description
 * Calculate the translate value for slide items
 * based on the slide item width and the number of slides
 */
const useCalculateTranslateValues = ({ items, carouselRef, windowSize, }) => {
    const [translateValuePerSlide, setTranslateValuePerSlide] = useState(0);
    const [firstItemTranslateValue, setFirstItemTranslateValue] = useState(0);
    const [lastItemTranslateValue, setLastItemTranslateValue] = useState(0);
    useEffect(() => {
        const { slideWidth } = getSlideWidth({ carouselRef });
        const { translateValuePerSlide: translateValuePerSlidePayload } = getTranslateValuePerSlide({
            slideWidth,
        });
        const firstItemTranslateValuePayload = -(translateValuePerSlidePayload +
            slideWidth -
            VISIBLE_SIBLING_SLIDE_WIDTH);
        const lastItemTranslateValuePayload = firstItemTranslateValuePayload - translateValuePerSlidePayload * (items.length - 1);
        setTranslateValuePerSlide(translateValuePerSlidePayload);
        setFirstItemTranslateValue(firstItemTranslateValuePayload);
        setLastItemTranslateValue(lastItemTranslateValuePayload);
    }, [items, windowSize]);
    return {
        translateValuePerSlide,
        firstItemTranslateValue,
        lastItemTranslateValue,
    };
};
const useApplyResizedTranslateValue = ({ windowSize, translateValueForCurrentActiveItem, carouselRef, setTranslateValue, }) => {
    useEffect(() => {
        if (!carouselRef.current)
            return;
        setTranslateValue(translateValueForCurrentActiveItem);
    }, [carouselRef.current, windowSize, translateValueForCurrentActiveItem]);
};
const useWindowResizeListener = () => {
    const [windowSize, setWindowSize] = useState(window.innerWidth);
    useEffect(() => {
        const handleResize = () => {
            setWindowSize(window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);
    return { windowSize };
};
const useInitializeCurrentSlideAndActiveData = ({ defaultActiveItemIndex, setCurrentSlideIndex, firstItemIndexInSlideClones, slideItems, setActiveItemData, setActiveItemId, }) => {
    useEffect(() => {
        const currentSlideIndex = firstItemIndexInSlideClones + defaultActiveItemIndex;
        setCurrentSlideIndex(currentSlideIndex);
        const currentItem = slideItems[currentSlideIndex];
        const currentItemData = get(currentItem, 'props.data');
        const currentItemId = get(currentItem, 'props.id');
        setActiveItemData(currentItemData);
        setActiveItemId(currentItemId);
    }, [defaultActiveItemIndex]);
};
// render functions
const renderSlides = ({ itemsLength, carouselRef, shouldStartTransition, translateValue, slideItems, currentSlideIndex, firstItemIndexInSlideClones, firstItemTranslateValue, lastItemTranslateValue, touchPosition, isSlideDisabled, translateValuePerSlide, setShouldStartTransition, setTranslateValue, setCurrentSlideIndex, setIsSlideDisabled, setTouchPosition, activeItemId, activeItemData, setActiveItemId, setActiveItemData, handleActiveItemChange, }) => (<S.CarouselView onTouchStart={(e) => handleTouchStart({
        firstTouch: e.touches[0],
        setTouchPosition,
    })} onTouchMove={(e) => handleTouchMove({
        firstTouch: e.touches[0],
        touchDown: touchPosition,
        isSlideDisabled,
        translateValuePerSlide,
        setTranslateValue,
        setShouldStartTransition,
        setCurrentSlideIndex,
        setIsSlideDisabled,
        setTouchPosition,
        slideItems,
        currentSlideIndex,
        activeItemId,
        activeItemData,
        setActiveItemId,
        setActiveItemData,
        handleActiveItemChange,
    })}>
    <S.LayoutCarouselItems ref={carouselRef} shouldStartTransition={shouldStartTransition} translateValue={translateValue} itemCount={slideItems.length} onTransitionEnd={() => handleTransitionEnd({
        itemsLength,
        currentSlideIndex,
        firstItemIndexInSlideClones,
        firstItemTranslateValue,
        lastItemTranslateValue,
        setShouldStartTransition,
        setTranslateValue,
        setCurrentSlideIndex,
        setIsSlideDisabled,
    })}>
      {slideItems}
    </S.LayoutCarouselItems>
  </S.CarouselView>);
const renderPrevNextButtons = ({ isSlideDisabled, translateValuePerSlide, setTranslateValue, setShouldStartTransition, setCurrentSlideIndex, setIsSlideDisabled, slideItems, currentSlideIndex, activeItemId, activeItemData, setActiveItemId, setActiveItemData, handleActiveItemChange, }) => (<>
    <S.PrevButton onClick={() => handleSlide({
        eventType: SLIDE_EVENT_TYPE.PREV,
        isSlideDisabled,
        translateValuePerSlide,
        slideItems,
        currentSlideIndex,
        activeItemId,
        activeItemData,
        setActiveItemId,
        setActiveItemData,
        handleActiveItemChange,
        setTranslateValue,
        setShouldStartTransition,
        setCurrentSlideIndex,
        setIsSlideDisabled,
    })}>
      <ChevronLeft size='21px' color={SEMANTIC_COLORS.PRIMARY_ICON}/>
    </S.PrevButton>
    <S.NextButton onClick={() => handleSlide({
        eventType: SLIDE_EVENT_TYPE.NEXT,
        isSlideDisabled,
        translateValuePerSlide,
        slideItems,
        currentSlideIndex,
        activeItemId,
        activeItemData,
        setActiveItemId,
        setActiveItemData,
        handleActiveItemChange,
        setTranslateValue,
        setShouldStartTransition,
        setCurrentSlideIndex,
        setIsSlideDisabled,
    })}>
      <ChevronRight size='21px' color={SEMANTIC_COLORS.PRIMARY_ICON}/>
    </S.NextButton>
  </>);
const renderNavigationDots = ({ processedItems, activeItemId, }) => (<S.LayoutNavigationDots>
    {processedItems.map((item, i) => {
        const itemId = get(item, 'props.id');
        return <S.NavigationDot isActive={itemId === activeItemId} key={i}/>;
    })}
  </S.LayoutNavigationDots>);
export default function InfiniteCarousel({ items, handleActiveItemChange, defaultActiveItemIndex = 0, }) {
    const [activeItemData, setActiveItemData] = useState(null);
    const [activeItemId, setActiveItemId] = useState('');
    const { slideItems, processedItems } = getSlideItems({ items, activeItemId });
    const firstItemIndexInSlideClones = 2;
    // The currentSlideIndex is zero-based and is the index of the item in the cloned items array
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const [translateValue, setTranslateValue] = useState(0);
    const [shouldStartTransition, setShouldStartTransition] = useState(false); // control CSS transition
    const [isSlideDisabled, setIsSlideDisabled] = useState(false);
    const [touchPosition, setTouchPosition] = useState(null); // touch.clientX
    const carouselRef = useRef(null); // used for accessing the slide item width
    useInitializeCurrentSlideAndActiveData({
        defaultActiveItemIndex,
        setCurrentSlideIndex,
        firstItemIndexInSlideClones,
        slideItems,
        setActiveItemData,
        setActiveItemId,
    });
    const { windowSize } = useWindowResizeListener();
    const { translateValuePerSlide, firstItemTranslateValue, lastItemTranslateValue } = useCalculateTranslateValues({
        items,
        carouselRef,
        windowSize,
    });
    const { slideWidth } = getSlideWidth({ carouselRef });
    const { translateValueForCurrentActiveItem } = getTranslateValueForCurrentActiveItem({
        slideWidth,
        currentSlideIndex,
        translateValuePerSlide,
    });
    useApplyResizedTranslateValue({
        windowSize,
        translateValueForCurrentActiveItem,
        carouselRef,
        setTranslateValue,
    });
    return (<>
      <S.CarouselContainer>
        {renderSlides({
            itemsLength: items.length,
            carouselRef,
            shouldStartTransition,
            translateValue,
            slideItems,
            currentSlideIndex,
            firstItemIndexInSlideClones,
            firstItemTranslateValue,
            lastItemTranslateValue,
            touchPosition,
            isSlideDisabled,
            translateValuePerSlide,
            setShouldStartTransition,
            setTranslateValue,
            setCurrentSlideIndex,
            setIsSlideDisabled,
            setTouchPosition,
            activeItemId,
            activeItemData,
            setActiveItemId,
            setActiveItemData,
            handleActiveItemChange,
        })}
        {renderPrevNextButtons({
            isSlideDisabled,
            translateValuePerSlide,
            setTranslateValue,
            setShouldStartTransition,
            setCurrentSlideIndex,
            setIsSlideDisabled,
            slideItems,
            currentSlideIndex,
            activeItemId,
            activeItemData,
            setActiveItemId,
            setActiveItemData,
            handleActiveItemChange,
        })}
      </S.CarouselContainer>
      {renderNavigationDots({ processedItems, activeItemId })}
    </>);
}
